/**
 * V2 — clear-local-first session end (admin `endAdminSession` parity).
 * Drop React memory immediately, then best-effort logout revoke, then bus/cache/redirect.
 * Logout API soft-fail never skips wipe (no dead visibility-retry flag).
 */

import type { QueryClient } from '@tanstack/react-query';
import type { CustomerAuthCrossTabMessage } from 'pi-kiosk-shared/crossTab';
import { isPlatformChromeTenant } from '../../app/platformChromeTenant.js';
import type { RuntimeConfig } from '../../config/runtimeConfig.js';
import { resolvePostLogoutLandingPath } from '../../features/shop/resolveShopHref.js';
import { clearCustomerAccountCache } from '../api/clearCustomerAccountCache.js';
import { publishCustomerAuth } from '../crossTab/customerCrossTab.js';
import { customerLogger } from '../logging/customerLogger.js';
import { clearStepUpVerified } from './customerStepUp.js';
import { customerAuthCsrfHeaders } from './customerAuthCsrf.js';
import { stormAwareFetch } from '../http/stormAwareFetch.js';
import { notifySessionEnded, type CustomerAuthEndReason } from './customerAuthLifecycle.js';
import { clearCustomerSessionStarted } from './useCustomerSessionTimeouts.js';
import { clearJourneyCheckpoint } from '../../app/pwa/journeyCheckpoint.js';
import { clearAllPaymentSurfaceAuthSeenForTenant } from '../../app/pwa/paymentSurfaceAuthSeen.js';
import {
  beginSessionEndingLatch,
  endSessionEndingLatch,
} from './customerSessionEndingLatch.js';
import { resetSessionRefreshForTests } from './sessionRefresh.js';

export type EndCustomerSessionReason = CustomerAuthEndReason;

export {
  beginSessionEndingLatch,
  endSessionEndingLatch,
  isSessionEndingLatchActive,
} from './customerSessionEndingLatch.js';

let endSessionInFlight: Promise<void> | null = null;

function resolveLogoutBusScope(
  tenantCode: string,
  api: 'logout' | 'logout-all' | undefined,
): 'platform' | 'tenant' | 'global' {
  if (api === 'logout-all') {
    return 'global';
  }
  if (isPlatformChromeTenant(tenantCode)) {
    return 'platform';
  }
  return 'tenant';
}

function assertCustomerAuthBusHasNoJwt(message: CustomerAuthCrossTabMessage): void {
  const record = message as Record<string, unknown>;
  for (const key of ['accessToken', 'refreshToken', 'token', 'idToken'] as const) {
    const value = record[key];
    if (typeof value === 'string' && value.length > 0) {
      throw new Error(
        `Customer auth bus must not carry ${key}; peers revalidate /me only`,
      );
    }
  }
}

/**
 * POST logout best-effort revoke after clear-local-first.
 * httpOnly cookies still attach via credentials:include until server clears them.
 * Definitive 4xx (except 429) counts as cleared server-side — admin parity.
 */
async function postLogoutApi(
  config: RuntimeConfig,
  api: 'logout' | 'logout-all',
): Promise<boolean> {
  const base = config.apiBaseUrl.replace(/\/+$/, '');
  const path =
    api === 'logout-all'
      ? '/api/v1/customer-auth/logout-all'
      : '/api/v1/customer-auth/logout';
  const url = `${base}${path}`;
  const idempotencyKey =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `idem-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

  try {
    const response = await stormAwareFetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'Idempotency-Key': idempotencyKey,
        ...customerAuthCsrfHeaders(),
      },
      body: JSON.stringify({}),
    });
    if (
      response.ok ||
      (response.status >= 400 && response.status < 500 && response.status !== 429)
    ) {
      return true;
    }
    customerLogger.warn(
      'logout_api_failed',
      new Error(`logout revoke soft-failed status=${response.status}`),
    );
    return false;
  } catch (err: unknown) {
    customerLogger.warn('logout_api_failed', err);
    return false;
  }
}

export interface EndCustomerSessionOptions {
  tenantCode: string;
  broadcast: boolean;
  callApi: boolean;
  api?: 'logout' | 'logout-all';
  reason?: EndCustomerSessionReason;
  config: RuntimeConfig;
  queryClient: QueryClient;
  clearMemorySession: () => void;
  setSessionHydrated: (value: boolean) => void;
}

export async function endCustomerSession(opts: EndCustomerSessionOptions): Promise<void> {
  if (endSessionInFlight !== null) {
    return endSessionInFlight;
  }

  endSessionInFlight = (async () => {
    try {
      await runEndCustomerSession(opts);
    } finally {
      endSessionInFlight = null;
    }
  })();

  return endSessionInFlight;
}

async function runEndCustomerSession(opts: EndCustomerSessionOptions): Promise<void> {
  const reason = opts.reason ?? 'logout';

  // Latch before clear so concurrent account gates /me cannot revive mid-end.
  beginSessionEndingLatch();
  try {
    // G1 — drop React auth immediately before any logout await (admin clear-local-first).
    opts.clearMemorySession();
    opts.setSessionHydrated(true);

    if (opts.callApi) {
      // Best-effort revoke — soft-fail must not skip wipe / bus / redirect.
      await postLogoutApi(opts.config, opts.api ?? 'logout');
    }

    if (opts.broadcast) {
      const logoutSignal: CustomerAuthCrossTabMessage = {
        type: 'logout',
        tenantCode: opts.tenantCode,
        scope: resolveLogoutBusScope(opts.tenantCode, opts.api),
      };
      assertCustomerAuthBusHasNoJwt(logoutSignal);
      publishCustomerAuth(logoutSignal);
    }

    resetSessionRefreshForTests();

    const pathnameBeforeWipe =
      typeof window !== 'undefined' ? window.location.pathname : '';

    clearCustomerAccountCache(opts.queryClient, opts.tenantCode);
    // G12 — removeQueries only; invalidateQueries re-arms observers → post-logout /me.

    clearStepUpVerified();
    clearCustomerSessionStarted();
    clearJourneyCheckpoint(opts.tenantCode);
    clearAllPaymentSurfaceAuthSeenForTenant(opts.tenantCode);

    customerLogger.info('customer.auth.session_ended', {
      feature: 'customer-auth',
      operation: 'session_ended',
      reason,
    });
    notifySessionEnded(reason);

    if (
      typeof window !== 'undefined' &&
      (/^\/account(?:\/|$)/.test(pathnameBeforeWipe) ||
        /^\/[^/]+\/account(?:\/|$)/.test(pathnameBeforeWipe))
    ) {
      const landing = resolvePostLogoutLandingPath(pathnameBeforeWipe, opts.tenantCode);
      if (window.location.pathname !== landing) {
        window.location.replace(landing);
      }
    }
  } finally {
    endSessionEndingLatch();
  }
}

export { markCustomerSessionStarted } from './useCustomerSessionTimeouts.js';
